import { DEFAULT_TEMPLATE_ID, type FullCV, Prisma, type TemplateId } from '@cibli/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCVTemplateContext } from '@/features/common/contexts/cv-template.context';
import { fetchCV, generateCV } from '@/services/interview';

// Simple structured logger for React hooks
const logger = {
  error: (message: string, ...args: unknown[]) => console.error(`[useCVEdit] ${message}`, ...args),
};

export function useCVEdit(cvId: string | undefined) {
  const { t } = useTranslation();
  const [interviewId, setInterviewId] = useState<string | null>(null);
  const [cv, setCv] = useState<FullCV | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    cvTemplateId,
    cvTemplateParams,
    setCvTemplateId,
    setCvTemplateParams,
    setCurrentLocaleCv,
  } = useCVTemplateContext();

  useEffect(() => {
    if (!cvId) return;

    const loadCV = async () => {
      try {
        setIsLoading(true);
        const data = await fetchCV(cvId);
        setCv(data);
        setInterviewId(data.interviewId);
        setCvTemplateId((data.template as TemplateId) || DEFAULT_TEMPLATE_ID);
        setCvTemplateParams((data.templateParams as Record<string, unknown>) || {});
        if (data.locale?.codeLong) {
          setCurrentLocaleCv(data.locale.codeLong);
        }
      } catch (err) {
        logger.error('Failed to load CV', err);
        setError(t('dashboard.cvEdit.loadError'));
      } finally {
        setIsLoading(false);
      }
    };

    void loadCV();
  }, [cvId, setCvTemplateId, setCvTemplateParams, setCurrentLocaleCv, t]);

  const handleSave = async () => {
    if (!cv) return;
    if (!interviewId) {
      setError(t('dashboard.cvEdit.missingInterview'));
      return;
    }

    const stripCvRefs = <T extends Record<string, unknown>>(item: T) => {
      const { id, cvId, ...rest } = item;
      void id;
      void cvId;
      return rest;
    };

    try {
      setIsSaving(true);
      const sanitizedCv: FullCV = {
        ...cv,
        experiences: cv.experiences.map(exp => stripCvRefs(exp)),
        personalExperiences: cv.personalExperiences.map(exp => stripCvRefs(exp)),
        languages: cv.languages.map(lang => stripCvRefs(lang)),
        education: cv.education.map(edu => stripCvRefs(edu)),
      };

      // Save CV in original language
      await generateCV(interviewId, {
        ...sanitizedCv,
        interviewId,
        template: cvTemplateId || DEFAULT_TEMPLATE_ID,
        templateParams: (cvTemplateParams || {}) as Prisma.JsonValue,
      });

      toast.success(t('dashboard.cvEdit.success'));
    } catch (err) {
      logger.error('Failed to save CV', err);
      toast.error(t('dashboard.cvEdit.error'));
    } finally {
      setIsSaving(false);
    }
  };

  return {
    cv,
    setCv,
    isLoading,
    isSaving,
    error,
    handleSave,
  };
}
